import { useCallback, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

// Make sure we're using the correct public key
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

interface CreateCheckoutSessionParams {
  priceId: string;
}

export const useStripeCheckout = () => {
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const createCheckoutSession = useCallback(async ({ priceId }: CreateCheckoutSessionParams) => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (!user) {
        throw new Error('User must be logged in to make a purchase');
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      if (!apiUrl) {
        throw new Error('API URL is not configured');
      }

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Log the price ID being used
      console.log('Creating checkout session for price:', priceId);

      // Send request to create checkout session
      const response = await axios.post(
        `${apiUrl}/api/stripe/create-checkout-session`,
        {
          priceId,
          successUrl: `${window.location.origin}/scene-detection?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/pricing?canceled=true`
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Log the response for debugging
      console.log('Checkout session response:', response.data);

      if (!response.data || !response.data.sessionId) {
        console.error('Invalid server response:', response.data);
        throw new Error('Invalid response from server');
      }

      // Get Stripe instance and verify it loaded
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Failed to load Stripe - check public key configuration');
      }

      // Log before redirect
      console.log('Redirecting to checkout with session ID:', response.data.sessionId);

      // Redirect to Stripe Checkout
      const { error } = await stripe.redirectToCheckout({
        sessionId: response.data.sessionId
      });

      if (error) {
        console.error('Stripe redirect error:', error);
        throw error;
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      if (axios.isAxiosError(err) && err.response?.data) {
        console.error('Server error details:', err.response.data);
        const errorMessage = err.response.data.message || err.response.data.error || err.message;
        setError(errorMessage);
        throw new Error(errorMessage);
      }
      const errorMessage = err instanceof Error ? err.message : 'Failed to create checkout session';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  return {
    createCheckoutSession,
    error,
    isLoading
  };
}; 